import styled, { withTheme, DefaultTheme } from 'styled-components';
import { useState, useEffect, useMemo } from 'react';
import { SvgLoader, SvgProxy } from 'react-svgmt';
import { useRouter } from 'next/router';
import { useMediaQuery } from 'react-responsive';
import { Breadcrumb, Button } from 'antd';
import tippy from 'tippy.js';

import { canonizeSearchQuery } from '../../common/helpers/searchQuery.utils';
import useI18n from '../../common/hooks/useI18n';
import { IMapArea } from '../../common/model/mapArea.model';
import { toSeoUrl } from '../../common/helpers';

type Props = {
  query: string;
  mapAreas: IMapArea[];
  theme: DefaultTheme;
};

const ROOT_MAP_ID = '0';
const PHASE_TWO_START_PERCENTAGE = 35;
const ANIMATION_DURATION = 500;

const Title = styled.h3`
  ${(props) => props.theme.font.h2}
  color: ${(props) => props.theme.colors.secondary};
`;

const Divider = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  border-top: 1px solid #e0e0e0;
  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-bottom: 0;
  }
`;

const Container = styled.div`
  position: relative;
  height: 540px;

  @media ${(props) => props.theme.breakpoints.lgu} {
    padding: 24px;
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
    border-radius: ${(props) => props.theme.borderRadius};
  }

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-bottom: 24px;
    margin-top: 24px;
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    height: unset;
  }
`;

const Placeholder = styled.div`
  height: 600px;
`;

const BreadcrumbComponent = styled(Breadcrumb)`
  z-index: 199;
  position: absolute;
  top: 24px;
  left: 24px;

  @media ${(props) => props.theme.breakpoints.smd} {
    display: none;
  }
`;

const BackButton = styled.img`
  z-index: 199;
  position: absolute;
  cursor: pointer;
  top: calc(50% - 9px);
  left: 24px;
  height: 18px;
  width: 18px;
  color: ${(props) => props.theme.colors.secondary};

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-left: -40px;
  }
`;

const MapContainer = styled.div<{
  animationEnabled: boolean;
  PHASE_TWO_START_PERCENTAGE: number;
  ANIMATION_DURATION: number;
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  min-height: 0;
  max-height: 100%;

  & .text {
    pointer-events: none;
  }

  @keyframes example {
    0% {
      transform: scale(1);
      animation-timing-function: cubic-bezier(0.35, 0, 1, 0.59);
    }
    ${(props) => props.PHASE_TWO_START_PERCENTAGE}% {
      transform: scale(2);
      animation-timing-function: linear;
    }
    ${(props) => props.PHASE_TWO_START_PERCENTAGE}.001% {
      transform: scale(0);
      animation-timing-function: cubic-bezier(0, 0.35, 0.59, 1);
    }
    100% {
      transform: scale(1);
      animation-timing-function: linear;
    }
  }

  & svg {
    max-width: min(100%, 600px);
    height: 433px;
    animation-name: ${(props) => (props.animationEnabled ? 'example' : 'none')};
    animation-duration: ${(props) => props.ANIMATION_DURATION}ms;

    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  & .zone {
    outline: none;
  }

  & > svg {
    -webkit-filter: drop-shadow(0px 6px 14px rgba(0, 0, 0, 0.16));
    filter: drop-shadow(0px 6px 14px rgba(0, 0, 0, 0.16));
  }

  @media ${(props) => props.theme.breakpoints.mdu} {
    & .zone path:hover {
      fill: ${(props) => props.theme.colors.secondary} !important;
    }
    & .zone polygon:hover {
      fill: ${(props) => props.theme.colors.secondary} !important;
    }
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    & svg {
      height: 84vw;
    }

    & .zone path:active {
      fill: ${(props) => props.theme.colors.secondary} !important;
    }
    & .zone polygon:active {
      fill: ${(props) => props.theme.colors.secondary} !important;
    }
  }
`;

const computeInitialMapId = (
  query: string,
  mapAreasDict: Record<string, IMapArea>
): string => {
  const matchingMapId =
    query.length > 1
      ? Object.keys(mapAreasDict).find((mapId) =>
          mapId.toLowerCase().includes(query.toLowerCase().replaceAll('-', '_'))
        )
      : undefined;
  return matchingMapId || ROOT_MAP_ID;
};

const HierarchicalMap = ({ query, mapAreas, theme }: Props): JSX.Element => {
  const router = useRouter();
  const i18n = useI18n();

  const mapAreasDict: Record<string, IMapArea> = useMemo(() => {
    return mapAreas.reduce((dict, mapArea) => {
      dict[mapArea.id] = mapArea;
      return dict;
    }, {});
  }, [mapAreas]);

  const initialMapId = useMemo(() => computeInitialMapId(query, mapAreasDict), [
    query,
    mapAreasDict,
  ]);

  const [currentMapId, _setCurrentMapId] = useState(initialMapId);
  const [stateHistory, setStateHistory] = useState([]);
  const [animationEnabled, setAnimationEnabled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  let breadcrumbStates;
  if (!(currentMapId in stateHistory)) {
    breadcrumbStates = [...stateHistory, currentMapId];
  } else {
    breadcrumbStates = stateHistory;
  }
  breadcrumbStates = breadcrumbStates.slice(1);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isMdu = !useMediaQuery({ query: theme.breakpoints.smd });

  const setCurrentMapId = (nextId) => {
    if (nextId !== currentMapId) {
      setStateHistory([...stateHistory, currentMapId]);
    }
    return _setCurrentMapId(nextId);
  };

  const pauseAnimation = () => {
    setAnimationEnabled(false);
  };

  const onBackButtonClick = () => {
    if (stateHistory.length > 0) {
      const nextId = stateHistory[stateHistory.length - 1];
      setStateHistory(stateHistory.slice(0, -1));
      _setCurrentMapId(nextId);
    }
  };

  const routeToSearch = (q: string) => {
    router.push({
      pathname: `/areas/venta-viviendas/${toSeoUrl(canonizeSearchQuery(q))}`,
    });
  };

  const configureSVGZones = (svgElements: SVGGeometryElement[]) => {
    configureTippy();

    for (const svgElement of svgElements) {
      const elementId = svgElement.id;
      if (!(elementId in mapAreasDict)) {
        console.log(`WARNING: ${elementId} missing from zones definition`);
        const paths = svgElement.querySelectorAll('path');
        for (let i = 0; i < paths.length; i++) {
          const path = paths[i];
          path.style.fill = '#ef9981';
        }
        continue;
      }

      const mapArea: IMapArea = mapAreasDict[elementId];

      const paths = svgElement.querySelectorAll('path, polygon');
      for (let i = 0; i < paths.length; i++) {
        const path = paths[i] as SVGPolygonElement | SVGPathElement;
        if (mapArea.hasFlats) {
          path.style.fill = '#e43c2f';
          path.style.cursor = 'pointer';
        } else {
          path.style.fill = '#ef9981';
        }
      }

      svgElement.addEventListener('click', () => {
        if (mapArea.url && mapArea.hasFlats) {
          setAnimationEnabled(true);
          setTimeout(() => {
            setCurrentMapId(elementId);
          }, (ANIMATION_DURATION * PHASE_TWO_START_PERCENTAGE) / 100);
        } else if (mapArea.hasFlats) {
          routeToSearch(elementId);
        } else {
          console.log('Zone selected has no flats');
        }
      });
    }
  };

  const configureTippy = () => {
    [...document.querySelectorAll('svg .zone')].forEach((node) => {
      if (node['_tippy']) {
        node['_tippy'].destroy();
      }
    });
    tippy('svg .zone', {
      content: (reference) => canonizeSearchQuery(reference.id),
      theme: 'dark',
      touch: ['hold', 300],
    });
  };

  if (!(currentMapId in mapAreasDict)) {
    setStateHistory([]);
    _setCurrentMapId('0');
  }

  const titleKey = 'mapTitle';
  const mapAreaUrls = Object.values(mapAreasDict)
    .filter((mapArea) => mapArea.url)
    .map((mapArea) => mapArea.url);

  return (
    <div>
      <Title>
        {i18n.t(`dynamic.homePage.${query}.${titleKey}`) ||
          i18n.t(`homePage.${titleKey}`)}
      </Title>
      <Divider />
      <Container>
        {isMounted ? (
          <>
            {stateHistory.length > 0 ? (
              <BackButton
                as="img"
                className="anticon"
                src={'/images/prev_no_circle.svg'}
                onClick={onBackButtonClick}
              />
            ) : null}
            {breadcrumbStates.length > 0 ? (
              <BreadcrumbComponent separator=">">
                {breadcrumbStates.map((state, i) => {
                  return (
                    <Breadcrumb.Item key={state}>
                      <span
                        style={{
                          fontWeight:
                            i === breadcrumbStates.length - 1 ? 600 : 400,
                        }}
                      >{` ${canonizeSearchQuery(state)} `}</span>
                    </Breadcrumb.Item>
                  );
                })}
              </BreadcrumbComponent>
            ) : null}
            <MapContainer
              as="div"
              animationEnabled={animationEnabled}
              onAnimationEnd={pauseAnimation}
              PHASE_TWO_START_PERCENTAGE={PHASE_TWO_START_PERCENTAGE}
              ANIMATION_DURATION={ANIMATION_DURATION}
              style={{
                paddingTop:
                  isMdu && currentMapId !== ROOT_MAP_ID ? '24px' : 'unset',
              }}
            >
              <SvgLoader path={mapAreasDict[currentMapId].url}>
                <SvgProxy
                  selector=".zone"
                  onElementSelected={configureSVGZones}
                />
              </SvgLoader>
              {currentMapId !== initialMapId && (
                <Button
                  style={{
                    marginTop: '12px',
                  }}
                  type="primary"
                  size="large"
                  onClick={() => {
                    routeToSearch(currentMapId);
                  }}
                >
                  {i18n.t('homePage.map.searchHere')}
                </Button>
              )}
            </MapContainer>
          </>
        ) : (
          <Placeholder />
        )}
      </Container>
      {mapAreaUrls.map((url) => (
        <link key={url} rel="lazyload" href={url} as="image" />
      ))}
    </div>
  );
};

export default withTheme(HierarchicalMap);
